import { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import type { Editor } from '@tiptap/react';

export type EditorSource = 'create' | 'edit' | 'description' | null;

export interface SelectionRange {
  start: number;
  end: number;
}

export interface DeleteMatch {
  id: string;
  text: string;
  startIndex: number;
  endIndex: number;
}

interface EditorContextType {
  // Editor principal activo (el que tiene foco o el más reciente)
  activeEditor: Editor | null;
  setActiveEditor: (editor: Editor | null) => void;
  
  // Editor secundario (para páginas con 2 editores como description + generatedReport)
  secondaryEditor: Editor | null;
  setSecondaryEditor: (editor: Editor | null) => void;
  
  // Fuente del editor activo (create = ReportCreatePage, edit = ReportEditPage)
  editorSource: EditorSource;
  setEditorSource: (source: EditorSource) => void;
  
  // Callback para actualizar contenido del editor principal
  onContentChange: ((content: string) => void) | null;
  setOnContentChange: (callback: ((content: string) => void) | null) => void;
  
  // Callback para actualizar contenido del editor secundario
  onSecondaryContentChange: ((content: string) => void) | null;
  setOnSecondaryContentChange: (callback: ((content: string) => void) | null) => void;
  
  // Selección actual
  selectionRange: SelectionRange | null;
  setSelectionRange: (range: SelectionRange | null) => void;
  
  // Fuente de la selección (primary = editor principal, secondary = editor secundario)
  selectionSource: 'primary' | 'secondary' | null;
  setSelectionSource: (source: 'primary' | 'secondary' | null) => void;
  
  // Elemento DOM seleccionado
  targetElement: Element | null;
  setTargetElement: (element: Element | null) => void;
  
  // Callback para generar reporte (solo para ReportCreatePage)
  triggerReportGeneration: ((customDescription?: string) => void) | null;
  setTriggerReportGeneration: (trigger: ((customDescription?: string) => void) | null) => void;
  
  // Estado de staging para eliminaciones
  deleteMatches: DeleteMatch[];
  setDeleteMatches: (matches: DeleteMatch[]) => void;
  deleteTarget: string;
  setDeleteTarget: (target: string) => void;
  isDeleteStaging: boolean;
  setIsDeleteStaging: (staging: boolean) => void;
  
  // Utilidades
  getActiveEditorContent: () => string;
  getSecondaryEditorContent: () => string;
  clearEditorState: () => void;
}

const EditorContext = createContext<EditorContextType | undefined>(undefined);

export function EditorProvider({ children }: { children: ReactNode }) {
  // Editores
  const [activeEditor, setActiveEditor] = useState<Editor | null>(null);
  const [secondaryEditor, setSecondaryEditor] = useState<Editor | null>(null);
  const [editorSource, setEditorSource] = useState<EditorSource>(null);
  
  // Callbacks de contenido
  const onContentChangeRef = useRef<((content: string) => void) | null>(null);
  const onSecondaryContentChangeRef = useRef<((content: string) => void) | null>(null);
  
  // Selección
  const [selectionRange, setSelectionRange] = useState<SelectionRange | null>(null);
  const [selectionSource, setSelectionSource] = useState<'primary' | 'secondary' | null>(null);
  const [targetElement, setTargetElement] = useState<Element | null>(null);
  
  // Generación de reporte
  const triggerReportGenerationRef = useRef<((customDescription?: string) => void) | null>(null);
  
  // Estado de staging para eliminaciones
  const [deleteMatches, setDeleteMatches] = useState<DeleteMatch[]>([]);
  const [deleteTarget, setDeleteTarget] = useState('');
  const [isDeleteStaging, setIsDeleteStaging] = useState(false);

  // Setters para refs (para evitar re-renders)
  const setOnContentChange = useCallback((callback: ((content: string) => void) | null) => {
    onContentChangeRef.current = callback;
  }, []);

  const setOnSecondaryContentChange = useCallback((callback: ((content: string) => void) | null) => {
    onSecondaryContentChangeRef.current = callback;
  }, []);

  const setTriggerReportGeneration = useCallback((trigger: ((customDescription?: string) => void) | null) => {
    triggerReportGenerationRef.current = trigger;
  }, []);

  // Utilidades
  const getActiveEditorContent = useCallback((): string => {
    if (activeEditor && !activeEditor.isDestroyed) {
      return activeEditor.getHTML();
    }
    return '';
  }, [activeEditor]);

  const getSecondaryEditorContent = useCallback((): string => {
    if (secondaryEditor && !secondaryEditor.isDestroyed) {
      return secondaryEditor.getHTML();
    }
    return '';
  }, [secondaryEditor]);

  const clearEditorState = useCallback(() => {
    setActiveEditor(null);
    setSecondaryEditor(null);
    setEditorSource(null);
    setSelectionRange(null);
    setSelectionSource(null);
    setTargetElement(null);
    setDeleteMatches([]);
    setDeleteTarget('');
    setIsDeleteStaging(false);
    onContentChangeRef.current = null;
    onSecondaryContentChangeRef.current = null;
    triggerReportGenerationRef.current = null;
  }, []);

  // Memorizar el value para evitar re-renders innecesarios
  const value = useMemo<EditorContextType>(() => ({
    activeEditor,
    setActiveEditor,
    secondaryEditor,
    setSecondaryEditor,
    editorSource,
    setEditorSource,
    onContentChange: onContentChangeRef.current,
    setOnContentChange,
    onSecondaryContentChange: onSecondaryContentChangeRef.current,
    setOnSecondaryContentChange,
    selectionRange,
    setSelectionRange,
    selectionSource,
    setSelectionSource,
    targetElement,
    setTargetElement,
    triggerReportGeneration: triggerReportGenerationRef.current,
    setTriggerReportGeneration,
    deleteMatches,
    setDeleteMatches,
    deleteTarget,
    setDeleteTarget,
    isDeleteStaging,
    setIsDeleteStaging,
    getActiveEditorContent,
    getSecondaryEditorContent,
    clearEditorState,
  }), [
    // Estados
    activeEditor,
    secondaryEditor,
    editorSource,
    selectionRange,
    selectionSource,
    targetElement,
    deleteMatches,
    deleteTarget,
    isDeleteStaging,
    // Callbacks memorizados
    setOnContentChange,
    setOnSecondaryContentChange,
    setTriggerReportGeneration,
    getActiveEditorContent,
    getSecondaryEditorContent,
    clearEditorState,
    // Nota: NO incluimos setters de useState ya que React los garantiza estables
  ]);

  return (
    <EditorContext.Provider value={value}>
      {children}
    </EditorContext.Provider>
  );
}
